import {
  type Band,
  describeRange,
  FormulaSyntaxError,
  parseFormula,
  type RangeSpec,
  references,
  type TemplateDoc,
  type TestRow,
} from '@quickreport/domain';
import { Button, Field, Input, Select } from '@quickreport/ui';
import { Plus, Trash2, X } from 'lucide-react';
import { codeFromLabel, updateTest } from './rows';

type RangeType = RangeSpec['type'];
const RANGE_TYPES: { value: RangeType; label: string }[] = [
  { value: 'between', label: 'Between min and max' },
  { value: 'lt', label: 'Below (<)' },
  { value: 'lte', label: 'At most (≤)' },
  { value: 'gt', label: 'Above (>)' },
  { value: 'gte', label: 'At least (≥)' },
  { value: 'bands', label: 'Named bands (e.g. Normal / Prediabetes)' },
  { value: 'qualitative', label: 'Expected word (e.g. Negative)' },
  { value: 'text', label: 'Printed text only, no flag' },
];

const num = (s: string): number | undefined =>
  s.trim() === '' || Number.isNaN(Number(s)) ? undefined : Number(s);
const YEAR = 365.25;

/** Settings for the selected test row: code, units, formula, ranges by sex and age, critical values. */
export function RowPanel({
  doc,
  row,
  onChange,
  onClose,
}: {
  doc: TemplateDoc;
  row: TestRow;
  onChange: (fn: (d: TemplateDoc) => TemplateDoc) => void;
  onClose: () => void;
}) {
  const set = (patch: Partial<TestRow>) => onChange((d) => updateTest(d, row.id, patch));
  const setRange = (i: number, r: RangeSpec) =>
    set({ ranges: row.ranges.map((x, j) => (j === i ? r : x)) });
  const formulaProblem = checkFormula(doc, row);
  const duplicate = doc.rows.some(
    (r) => r.kind === 'test' && r.id !== row.id && r.code === row.code,
  );

  return (
    <aside
      className="grid content-start items-start gap-4 p-4 md:grid-cols-2 xl:grid-cols-3"
      aria-label="Row settings"
    >
      <div className="col-span-full flex items-center justify-between">
        <h2 className="text-[15px] font-semibold">Row settings</h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close row settings"
          className="rounded p-1 text-slate hover:bg-canvas"
        >
          <X size={16} />
        </button>
      </div>

      <Field label="Investigation">
        <Input value={row.label} onChange={(e) => set({ label: e.target.value })} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Code"
          hint={
            duplicate ? (
              <span className="text-flag">Another row uses this code</span>
            ) : (
              'Used in formulas'
            )
          }
        >
          <Input
            value={row.code}
            onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
            onBlur={() => !row.code && set({ code: codeFromLabel(row.label) })}
            className="font-mono"
          />
        </Field>
        <Field label="Result type">
          <Select
            value={row.valueType}
            onChange={(e) => set({ valueType: e.target.value as TestRow['valueType'] })}
          >
            <option value="numeric">Number</option>
            <option value="text">Text</option>
            <option value="option">Pick from list</option>
          </Select>
        </Field>
        <Field label="Unit">
          <Input
            value={row.unit ?? ''}
            onChange={(e) => set({ unit: e.target.value || undefined })}
          />
        </Field>
        <Field label="Decimals">
          <Input
            type="number"
            min={0}
            max={6}
            value={row.decimals ?? ''}
            onChange={(e) => set({ decimals: num(e.target.value) })}
          />
        </Field>
        <Field label="Method">
          <Input
            value={row.method ?? ''}
            onChange={(e) => set({ method: e.target.value || undefined })}
          />
        </Field>
        <Field label="LOINC">
          <Input
            value={row.loinc ?? ''}
            onChange={(e) => set({ loinc: e.target.value || undefined })}
            className="font-mono"
          />
        </Field>
      </div>

      {row.valueType === 'option' ? (
        <>
          <Field label="Choices" hint="Separate with commas">
            <Input
              value={(row.options ?? []).join(', ')}
              onChange={(e) =>
                set({
                  options: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />
          </Field>
          <label className="flex items-start gap-2 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5 accent-brand"
              checked={row.allowOther === true}
              onChange={(e) => set({ allowOther: e.target.checked ? true : undefined })}
            />
            <span>
              <b>Also allow typing.</b> The choices are suggestions; the person can type any other
              value, for example a number.
            </span>
          </label>
        </>
      ) : null}

      <Field
        label="Formula (makes the row calculated)"
        hint={
          formulaProblem ? (
            <span className="text-flag">{formulaProblem}</span>
          ) : (
            'Use other rows’ codes, e.g. PCV * 10 / RBC'
          )
        }
      >
        <Input
          value={row.formula ?? ''}
          onChange={(e) => set({ formula: e.target.value || undefined })}
          className="font-mono"
          placeholder="none"
        />
      </Field>

      <div className="flex flex-wrap gap-x-4 gap-y-2">
        <Check
          label="Required before signing"
          checked={row.required}
          onChange={(v) => set({ required: v })}
        />
        <Check
          label="Print in bold"
          checked={Boolean(row.printBold)}
          onChange={(v) => set({ printBold: v || undefined })}
        />
        <Check
          label="Hide when empty"
          checked={Boolean(row.hideIfEmpty)}
          onChange={(v) => set({ hideIfEmpty: v || undefined })}
        />
      </div>

      <section className="grid gap-2">
        <div className="flex items-center justify-between">
          <h3 className="text-[13px] font-semibold">Reference ranges</h3>
          <Button
            size="sm"
            onClick={() => set({ ranges: [...row.ranges, { type: 'between', min: 0, max: 0 }] })}
          >
            <Plus size={13} /> Add range
          </Button>
        </div>
        {row.ranges.length === 0 ? (
          <p className="text-[12px] text-slate">No range: results in this row are never flagged.</p>
        ) : null}
        <p className="text-[12px] text-slate">
          The most specific range wins: one for the patient’s sex and age beats one for sex only,
          which beats one for everyone.
        </p>
        {row.ranges.map((r, i) => (
          <RangeEditor
            // biome-ignore lint/suspicious/noArrayIndexKey: ranges have no ids; order is their identity
            key={i}
            range={r}
            onChange={(next) => setRange(i, next)}
            onRemove={() => set({ ranges: row.ranges.filter((_, j) => j !== i) })}
          />
        ))}
      </section>

      <section className="grid grid-cols-2 gap-3">
        <h3 className="col-span-2 text-[13px] font-semibold">Critical values</h3>
        <Field label="Critical low (≤)">
          <Input
            inputMode="decimal"
            value={row.critical?.low ?? ''}
            onChange={(e) => set({ critical: { ...row.critical, low: num(e.target.value) } })}
          />
        </Field>
        <Field label="Critical high (≥)">
          <Input
            inputMode="decimal"
            value={row.critical?.high ?? ''}
            onChange={(e) => set({ critical: { ...row.critical, high: num(e.target.value) } })}
          />
        </Field>
      </section>
    </aside>
  );
}

function RangeEditor({
  range,
  onChange,
  onRemove,
}: {
  range: RangeSpec;
  onChange: (r: RangeSpec) => void;
  onRemove: () => void;
}) {
  const common = {
    sex: range.sex,
    ageMinDays: range.ageMinDays,
    ageMaxDays: range.ageMaxDays,
    display: range.display,
  };
  const changeType = (type: RangeType) => {
    const base = { ...common };
    if (type === 'between') onChange({ ...base, type, min: 0, max: 0 });
    else if (type === 'lt' || type === 'lte') onChange({ ...base, type, max: 0 });
    else if (type === 'gt' || type === 'gte') onChange({ ...base, type, min: 0 });
    else if (type === 'bands')
      onChange({
        ...base,
        type,
        bands: [
          { label: 'Normal', max: 0, flag: 'normal' },
          { label: 'High', min: 0, flag: 'high' },
        ],
      });
    else if (type === 'qualitative') onChange({ ...base, type, expected: ['Negative'] });
    else onChange({ ...base, type: 'text' });
  };

  return (
    <div className="grid gap-2 rounded-md border border-line p-2.5">
      <div className="flex items-center gap-2">
        <Select
          value={range.type}
          onChange={(e) => changeType(e.target.value as RangeType)}
          aria-label="Range type"
        >
          {RANGE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove range"
          className="rounded p-1 text-slate hover:text-flag"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {range.type === 'between' ? (
        <div className="grid grid-cols-2 gap-2">
          <Input
            aria-label="Minimum"
            inputMode="decimal"
            value={range.min}
            onChange={(e) => onChange({ ...range, min: num(e.target.value) ?? 0 })}
          />
          <Input
            aria-label="Maximum"
            inputMode="decimal"
            value={range.max}
            onChange={(e) => onChange({ ...range, max: num(e.target.value) ?? 0 })}
          />
        </div>
      ) : null}
      {range.type === 'lt' || range.type === 'lte' ? (
        <Input
          aria-label="Maximum"
          inputMode="decimal"
          value={range.max}
          onChange={(e) => onChange({ ...range, max: num(e.target.value) ?? 0 })}
        />
      ) : null}
      {range.type === 'gt' || range.type === 'gte' ? (
        <Input
          aria-label="Minimum"
          inputMode="decimal"
          value={range.min}
          onChange={(e) => onChange({ ...range, min: num(e.target.value) ?? 0 })}
        />
      ) : null}
      {range.type === 'qualitative' ? (
        <Input
          aria-label="Expected words"
          value={range.expected.join(', ')}
          onChange={(e) =>
            onChange({
              ...range,
              expected: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .slice(0) as [string, ...string[]],
            })
          }
        />
      ) : null}
      {range.type === 'bands' ? (
        <BandsEditor bands={range.bands} onChange={(bands) => onChange({ ...range, bands })} />
      ) : null}

      <div className="grid grid-cols-3 gap-2">
        <Select
          aria-label="Applies to sex"
          value={range.sex ?? ''}
          onChange={(e) =>
            onChange({ ...range, sex: (e.target.value || undefined) as RangeSpec['sex'] })
          }
        >
          <option value="">Everyone</option>
          <option value="M">Male</option>
          <option value="F">Female</option>
          <option value="O">Other</option>
        </Select>
        <Input
          aria-label="From age in years"
          placeholder="from age (y)"
          inputMode="decimal"
          value={range.ageMinDays === undefined ? '' : +(range.ageMinDays / YEAR).toFixed(2)}
          onChange={(e) =>
            onChange({
              ...range,
              ageMinDays:
                e.target.value === '' ? undefined : Math.round(Number(e.target.value) * YEAR),
            })
          }
        />
        <Input
          aria-label="To age in years"
          placeholder="to age (y)"
          inputMode="decimal"
          value={range.ageMaxDays === undefined ? '' : +(range.ageMaxDays / YEAR).toFixed(2)}
          onChange={(e) =>
            onChange({
              ...range,
              ageMaxDays:
                e.target.value === '' ? undefined : Math.round(Number(e.target.value) * YEAR),
            })
          }
        />
      </div>
      <Input
        aria-label="Printed text"
        placeholder={`Prints as: ${describeRange({ ...range, display: undefined } as RangeSpec) || '(nothing)'}`}
        value={range.display ?? ''}
        onChange={(e) => onChange({ ...range, display: e.target.value || undefined })}
      />
    </div>
  );
}

function BandsEditor({ bands, onChange }: { bands: Band[]; onChange: (b: Band[]) => void }) {
  const set = (i: number, patch: Partial<Band>) =>
    onChange(bands.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  return (
    <div className="grid gap-1.5">
      {bands.map((b, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: bands are positional
        <div key={i} className="grid grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-1">
          <Input
            aria-label="Band label"
            value={b.label}
            onChange={(e) => set(i, { label: e.target.value })}
          />
          <Input
            aria-label="From"
            placeholder="from"
            inputMode="decimal"
            value={b.min ?? ''}
            onChange={(e) => set(i, { min: num(e.target.value) })}
          />
          <Input
            aria-label="Below"
            placeholder="below"
            inputMode="decimal"
            value={b.max ?? ''}
            onChange={(e) => set(i, { max: num(e.target.value) })}
          />
          <Select
            aria-label="Flag"
            value={b.flag}
            onChange={(e) => set(i, { flag: e.target.value as Band['flag'] })}
          >
            <option value="normal">Normal</option>
            <option value="low">Low</option>
            <option value="high">High</option>
            <option value="abnormal">Abnormal</option>
          </Select>
          <button
            type="button"
            aria-label="Remove band"
            onClick={() => onChange(bands.filter((_, j) => j !== i))}
            className="px-1 text-slate hover:text-flag"
          >
            <X size={13} />
          </button>
        </div>
      ))}
      <Button size="sm" onClick={() => onChange([...bands, { label: 'Band', flag: 'normal' }])}>
        <Plus size={13} /> Add band
      </Button>
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[13px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-brand"
      />
      {label}
    </label>
  );
}

function checkFormula(doc: TemplateDoc, row: TestRow): string | null {
  if (!row.formula) return null;
  try {
    const refs = [...references(parseFormula(row.formula))];
    const codes = new Set(doc.rows.flatMap((r) => (r.kind === 'test' ? [r.code] : [])));
    const unknown = refs.filter((c) => !codes.has(c));
    if (unknown.length)
      return `Unknown code${unknown.length > 1 ? 's' : ''}: ${unknown.join(', ')}`;
    if (refs.includes(row.code)) return 'A row cannot use its own value';
    return null;
  } catch (e) {
    return e instanceof FormulaSyntaxError ? e.message : 'Invalid formula';
  }
}
